import {
  Box,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import useDevicesByIdList from 'hooks/useDevicesByIdList';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import type { FC } from 'react';
import { deviceIsCharger } from 'utils/common/deviceTypeUtils';

import DeviceGroupsIcon from 'components/Icons/DeviceGroupsIcon';
import DevicesIcon from 'components/Icons/DevicesIcon';
import CustomCheckbox from 'components/MuiCustom/CustomCheckbox';

export interface DeviceGroupProps {
  id: number;
  groupName: string;
  devices: number[] | undefined;
  selectedDeviceGroupIds: number[];
  setSelectedDeviceGroupIds: (selectedDeviceIds: number[]) => void;
}

const CustomBox = styled(Box)(({ theme }) => ({
  '&:not(:first-of-type)': {
    borderBottom: `1px solid ${theme.vars.palette.grey[100]}`,
  },
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1),
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
}));

const DeviceGroup: FC<DeviceGroupProps> = ({
  id,
  groupName,
  devices,
  selectedDeviceGroupIds,
  setSelectedDeviceGroupIds,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const deviceList = useDevicesByIdList(devices);
  const chargersCount = deviceList.filter(device =>
    deviceIsCharger(device.type),
  ).length;

  const handleSetSelectedDeviceGroupIds = useCallback(() => {
    if (selectedDeviceGroupIds.includes(id)) {
      setSelectedDeviceGroupIds(
        selectedDeviceGroupIds.filter(groupId => groupId !== id),
      );
    } else {
      setSelectedDeviceGroupIds([...selectedDeviceGroupIds, id]);
    }
  }, [id, selectedDeviceGroupIds, setSelectedDeviceGroupIds]);

  return (
    <CustomBox onClick={handleSetSelectedDeviceGroupIds}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
        >
          <CustomCheckbox
            checked={selectedDeviceGroupIds.includes(id)}
            data-testid="select-device-checkbox"
          />
          <DeviceGroupsIcon />
          <Box display="flex" flexDirection="column">
            <Typography data-testid="device-title">{groupName}</Typography>
          </Box>
        </Box>
        {chargersCount !== 0 && !smallScreen ? (
          <Box
            gap={1}
            maxWidth={200}
            flexDirection="row"
            display="flex"
            alignItems="center"
          >
            <DevicesIcon fontSize="small" sx={{ color: 'grey.500' }} />
            <Typography
              data-testid="device-group-names"
              variant="body1"
              noWrap
              color={theme.vars.palette.grey[500]}
            >
              {chargersCount}{' '}
              {t(chargersCount === 1 ? 'common:charger' : 'common:chargers')}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </CustomBox>
  );
};

export default DeviceGroup;
